/**
 * Subdomain Detection Utility
 * Detects if the app is running on a store subdomain
 */

import { getBackendUrl } from './api';

const API_URL = getBackendUrl();
const MAIN_DOMAIN = 'pedeefacil.shop';

/**
 * Extract subdomain from current hostname
 * @returns {string|null} The subdomain or null if on main domain
 */
export function extractSubdomain() {
  const hostname = window.location.hostname.toLowerCase();
  
  // Skip for localhost without subdomain
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }
  
  // Check for subdomain.localhost (dev environment)
  if (hostname.endsWith('.localhost')) {
    const subdomain = hostname.replace('.localhost', '');
    if (subdomain && subdomain !== 'www') {
      return subdomain;
    }
    return null;
  }
  
  // Check for production domain (subdomain.pedeefacil.shop)
  if (hostname.endsWith(`.${MAIN_DOMAIN}`)) {
    const subdomain = hostname.replace(`.${MAIN_DOMAIN}`, '');
    if (subdomain && subdomain !== 'www') {
      return subdomain;
    }
    return null;
  }
  
  // Check for preview environment (subdomain.xxx.preview.emergentagent.com)
  if (hostname.includes('.preview.emergentagent.com')) {
    const parts = hostname.split('.');
    // Format: subdomain.hash.preview.emergentagent.com
    if (parts.length > 4) {
      return parts[0];
    }
    return null;
  }
  
  return null;
}

/**
 * Check if we're on the main domain (not a store subdomain)
 * @returns {boolean}
 */
export function isMainDomain() {
  return extractSubdomain() === null;
}

/**
 * Check if we're on a store subdomain
 * @returns {boolean}
 */
export function isStoreSubdomain() {
  return extractSubdomain() !== null;
}

/**
 * Get the URL for a store's subdomain
 * @param {string} slug - The store's slug/subdomain
 * @returns {string} The full URL for the store
 */
export function getStoreUrl(slug) {
  const protocol = window.location.protocol;
  
  // For localhost development, use path-based URL
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return `${protocol}//${window.location.host}/menu/${slug}`;
  }
  
  // For production, use subdomain
  return `${protocol}//${slug}.${MAIN_DOMAIN}`;
}

/**
 * Get the share URL for a store (with OG support)
 * @param {string} slug - The store's slug/subdomain
 * @returns {string} The shareable URL with OG meta tags
 */
export function getStoreShareUrl(slug) {
  // Use the API endpoint for OG tags
  return `${API_URL}/api/og/loja/${slug}`;
}

export default {
  extractSubdomain,
  isMainDomain,
  isStoreSubdomain,
  getStoreUrl,
  getStoreShareUrl
};
